import React, { createContext, useContext, useState, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Info, CheckCircle, AlertCircle, AlertTriangle } from 'lucide-react';

export type ToastType = 'info' | 'success' | 'error' | 'warning';

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType, duration?: number) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((message: string, type: ToastType = 'info', duration: number = 3000) => {
    const id = Math.random().toString(36).substring(7);
    const toast: Toast = { id, message, type, duration };

    setToasts(prev => [...prev, toast]);

    if (duration > 0) {
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
      }, duration);
    }
  }, []);

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  const getIcon = (type: ToastType) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-4 h-4" />;
      case 'error':
        return <AlertCircle className="w-4 h-4" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4" />;
      default:
        return <Info className="w-4 h-4" />;
    }
  };

  const getColors = (type: ToastType) => {
    switch (type) {
      case 'success':
        return {
          bg: 'rgba(16, 185, 129, 0.08)',
          border: 'rgba(16, 185, 129, 0.3)',
          text: 'rgb(16, 185, 129)',
          icon: 'rgb(16, 185, 129)'
        };
      case 'error':
        return {
          bg: 'rgba(239, 68, 68, 0.08)',
          border: 'rgba(239, 68, 68, 0.3)',
          text: 'rgb(239, 68, 68)',
          icon: 'rgb(239, 68, 68)'
        };
      case 'warning':
        return {
          bg: 'rgba(245, 158, 11, 0.08)',
          border: 'rgba(245, 158, 11, 0.3)',
          text: 'rgb(245, 158, 11)',
          icon: 'rgb(245, 158, 11)'
        };
      default:
        return {
          bg: 'rgba(59, 130, 246, 0.08)',
          border: 'rgba(59, 130, 246, 0.3)',
          text: 'rgb(59, 130, 246)',
          icon: 'rgb(59, 130, 246)'
        };
    }
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}

      {/* Snackbar Container - Bottom Center */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] pointer-events-none">
        <AnimatePresence mode="wait">
          {toasts.length > 0 && (() => {
            const toast = toasts[0]; // Show only the first toast
            const colors = getColors(toast.type);
            return (
              <motion.div
                key={toast.id}
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                transition={{ type: 'spring', stiffness: 400, damping: 30, duration: 0.3 }}
                onClick={() => removeToast(toast.id)}
                className="pointer-events-auto flex items-center gap-3 px-4 py-2 rounded-lg shadow-xl border backdrop-blur-xl cursor-pointer hover:scale-[1.02] transition-transform"
                style={{
                  backgroundColor: colors.bg,
                  borderColor: colors.border,
                  minWidth: '280px',
                  maxWidth: '400px'
                }}
              >
                <div
                  className="flex-shrink-0"
                  style={{ color: colors.icon }}
                >
                  {getIcon(toast.type)}
                </div>

                <p
                  className="flex-1 font-medium text-sm"
                  style={{ color: colors.text }}
                >
                  {toast.message}
                </p>
              </motion.div>
            );
          })()}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
};
